/**
 * ProtectedRoute Component
 *
 * Wrapper component that redirects unauthenticated users to sign-in page
 * and preserves the intended destination for post-login redirect.
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

/**
 * ProtectedRoute component
 * @param {Object} props
 * @param {React.ReactNode} props.children - The protected component to render
 * @returns {React.ReactNode} - Either the protected content or redirect to signin
 */
export default function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Show nothing while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Redirect to sign-in if not authenticated, preserving the intended destination
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/signin"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  // User is authenticated, render the protected content
  return children;
}
